/**
 * First-run routing for journey-web (W2 §9.2).
 *
 * The decision is the shared, pure `resolvePostOnboardingDestination` in
 * `@tonnie/onboarding`; the route table is `first-destination-href.ts`; this
 * hook performs the navigation. There is no setup gate on the web: where a
 * company journey needs the citizen's residence, it is asked in place
 * (`useResidence`) before the invitation is accepted.
 */
import { useCallback } from 'react';
import { useLocation } from 'wouter';
import type { PostOnboardingDestination } from '@tonnie/onboarding';

import { useJourneyInstance } from '@/core/contexts';

import { firstDestinationHref } from '../lib/first-destination-href';

/**
 * Navigate to a resolved first destination.
 *
 * `resume-journey` names a specific journey, so that journey is made the active
 * instance before landing on the dashboard — otherwise a citizen with two
 * journeys would be shown whichever one localStorage last remembered.
 */
export function useGoToFirstDestination(): (
  destination: PostOnboardingDestination,
  /** History state for the destination — what an acceptance tells the page it lands on. */
  state?: Record<string, unknown>
) => void {
  const [, navigateTo] = useLocation();
  const { instances, setActiveInstance } = useJourneyInstance();

  return useCallback(
    (destination: PostOnboardingDestination, state?: Record<string, unknown>) => {
      const navigate = (to: string) => (state ? navigateTo(to, { state }) : navigateTo(to));
      const href = firstDestinationHref(destination);
      const target =
        destination.kind === 'resume-journey'
          ? instances.find((instance) => instance.id === destination.journeyId)
          : undefined;

      if (!target) {
        navigate(href);
        return;
      }

      // A registered switch guard may refuse (unsaved work in a hero flow). The
      // dashboard is the right landing either way, so navigate regardless of
      // the outcome rather than stranding the citizen where they were.
      void setActiveInstance(target).finally(() => navigate(href));
    },
    [instances, setActiveInstance, navigateTo]
  );
}
